import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { Textstyle } from "@/types/legend.types";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle } from "../core/oda";
import { noteGraphicsModified } from "../telemetry/updateTelemetry";
import { OpenMode } from "../core/constants";
import {
  DEFAULT_TEXT_STYLE,
  ensureTextStyleExists,
  findTextStyle,
} from "../database/textStyle";

/** Prefix of the dimension styles created by the dimension tool. */
export const DIMENSION_STYLE_PREFIX = "DR_COTE";

/** ',' — DIMDSEP takes the separator as a character code. */
const DECIMAL_COMMA = 44;
/** DIMZIN bit: suppress trailing zeros (3,50 → 3,5 ; 10,00 → 10). */
const SUPPRESS_TRAILING_ZEROS = 8;
/** DIMTAD: text above the dimension line. */
const TEXT_ABOVE = 1;

/**
 * Sizes of the style, for a text height of 1. The tool sets DIMSCALE on each
 * dimension to the text height of the active scale, which scales all of them
 * together — so a dimension keeps the size it was drawn at when the active
 * scale changes later, exactly like a text.
 */
const UNIT_SIZES = {
  dimtxt: 1,
  dimasz: 1,
  dimexo: 0.6,
  dimexe: 0.6,
  dimgap: 0.4,
};

/**
 * Text style of the dimensions written with a scale's font: same font, but no
 * fixed height. A text style with a fixed height (common in client DWGs)
 * overrides DIMTXT × DIMSCALE, so the text would keep that height whatever the
 * scale while the arrows follow it.
 */
function ensureDimensionTextStyle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  name: string,
  textstyle: Textstyle,
): DrawingModule.OdDbObjectId {
  const existingId = findTextStyle(module, db, name);
  if (existingId) {
    // Already created: only make sure nobody gave it a fixed height since.
    const existing = openAs(
      existingId,
      module.OdDbTextStyleTableRecord,
      OpenMode.kForWrite,
    );
    if (existing.textSize() !== 0) existing.setTextSize(0);
    deleteObj(existing);
    return existingId;
  }

  // The scale's own text style, when the drawing has it, knows how its font is
  // registered (TTF typeface or SHX file): copy it whole — font() can't be read
  // from JS, its out parameters aren't bound — rather than guess the font from
  // the legend, which may only give a file name.
  const sourceId = findTextStyle(module, db, textstyle.name);
  if (!sourceId) {
    const styleId = ensureTextStyleExists(module, db, { ...textstyle, name });
    const record = openAs(
      styleId,
      module.OdDbTextStyleTableRecord,
      OpenMode.kForWrite,
    );
    record.setTextSize(0);
    deleteObj(record);
    return styleId;
  }

  const source = openAs(
    sourceId,
    module.OdDbTextStyleTableRecord,
    OpenMode.kForRead,
  );
  const record = module.OdDbTextStyleTableRecord.createObject();
  record.copyFrom(source);
  deleteObj(source);
  deleteObj(sourceId);

  const nameStr = new module.OdString();
  nameStr.set(name);
  record.setName(nameStr);
  deleteObj(nameStr);
  record.setTextSize(0);

  const table = openAs(
    db.getTextStyleTableId(),
    module.OdDbTextStyleTable,
    OpenMode.kForWrite,
  );
  const styleId = table.add(
    record as unknown as DrawingModule.OdDbSymbolTableRecord,
  );
  deleteObj(record);
  deleteObj(table);
  return styleId;
}

/** Text style without fixed height of a font (DR_COTE_<font>). */
export function ensureDimensionFont(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  font: Textstyle,
): DrawingModule.OdDbObjectId {
  return ensureDimensionTextStyle(
    module,
    db,
    `${DIMENSION_STYLE_PREFIX}_${font.name}`,
    font,
  );
}

/** Rebuild the block of every model-space dimension using a style. */
function recomputeDimensionsOfStyle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  styleId: DrawingModule.OdDbObjectId,
): void {
  const styleHandle = getEntityHandle(styleId);
  const modelSpace = openAs(
    db.getModelSpaceId(),
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  const iterator = modelSpace.newIterator(true, true, false);
  for (; !iterator.done(); iterator.step(true, true)) {
    const entity = iterator.entity(OpenMode.kForWrite, false);
    const dimension = module.OdDbDimension.cast(entity) || null;
    if (dimension) {
      const dimStyleId = dimension.dimensionStyle();
      if (getEntityHandle(dimStyleId) === styleHandle) {
        dimension.recomputeDimBlock(true);
        entity.recordGraphicsModified(true);
      }
      deleteObj(dimStyleId);
      deleteObj(dimension);
    }
    deleteObj(entity);
  }
  deleteObj(iterator);
  deleteObj(modelSpace);
  noteGraphicsModified("Dimension");
}

/**
 * Get or create the dimension style of a scale, named after its text style
 * (DR_COTE_<text style>), the size coming from DIMSCALE. An existing style
 * keeps its settings (the user may have tuned it in a CAD software), except
 * its text style, always reset to the one without fixed height.
 */
export function ensureDimensionStyle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  textstyle: Textstyle | undefined,
): DrawingModule.OdDbObjectId {
  const font = textstyle ?? DEFAULT_TEXT_STYLE;
  const name = `${DIMENSION_STYLE_PREFIX}_${font.name}`;
  const textStyleId = ensureDimensionFont(module, db, font);
  const textStylePointer = new module.OdDbHardPointerId(textStyleId);

  const table = openAs(
    db.getDimStyleTableId(),
    module.OdDbDimStyleTable,
    OpenMode.kForWrite,
  );
  const nameStr = new module.OdString();
  nameStr.set(name);

  try {
    if (table.has1(nameStr)) {
      const styleId = table.getAt3(nameStr, false);
      const existing = openAs(
        styleId,
        module.OdDbDimStyleTableRecord,
        OpenMode.kForWrite,
      );
      const currentTextStyle = existing.dimtxsty();
      const isRepair =
        getEntityHandle(currentTextStyle) !== getEntityHandle(textStyleId);
      deleteObj(currentTextStyle);
      if (isRepair) existing.setDimtxsty(textStylePointer);
      deleteObj(existing);
      // Dimensions keep their block as last computed: those drawn while the
      // style had a fixed-height font only show the new text once rebuilt.
      if (isRepair) recomputeDimensionsOfStyle(module, db, styleId);
      return styleId;
    }

    const record = module.OdDbDimStyleTableRecord.createObject();
    record.setName(nameStr);
    record.setDimtxt(UNIT_SIZES.dimtxt);
    record.setDimasz(UNIT_SIZES.dimasz);
    record.setDimexo(UNIT_SIZES.dimexo);
    record.setDimexe(UNIT_SIZES.dimexe);
    record.setDimgap(UNIT_SIZES.dimgap);
    record.setDimtad(TEXT_ABOVE);
    // Text along the dimension line, also for vertical/aligned dimensions.
    record.setDimtih(false);
    record.setDimtoh(false);
    record.setDimdec(2);
    record.setDimdsep(DECIMAL_COMMA);
    record.setDimzin(SUPPRESS_TRAILING_ZEROS);
    record.setDimtxsty(textStylePointer);

    const styleId = table.add(
      record as unknown as DrawingModule.OdDbSymbolTableRecord,
    );
    deleteObj(record);
    return styleId;
  } finally {
    deleteObj(nameStr);
    deleteObj(table);
    deleteObj(textStylePointer);
    deleteObj(textStyleId);
  }
}
